'use client'

import { resolveBackendFileUrl } from '@/lib/api/config'
import { useWorkspaceStore } from '@/stores/workspace-store'

type ListedWorkspace = {
  id: string
  logoUrl?: string | null
  companyLogoUrl?: string | null
  darkLogoUrl?: string | null
  companyDarkLogoUrl?: string | null
}

/**
 * The customer's logo for the workspace on screen (owner, 2026-10-28): its
 * own, else its company's, as GET /workspaces/my names it; in the All
 * workspaces view, the company's. Absolute (an upload is served by the API).
 * Null when there is none.
 */
export function workspaceLogoOf(
  workspaces: readonly ListedWorkspace[],
  activeWorkspaceId: string | null,
): string | null {
  if (activeWorkspaceId === 'all') return resolveBackendFileUrl(workspaces[0]?.companyLogoUrl)
  return resolveBackendFileUrl(workspaces.find((w) => w.id === activeWorkspaceId)?.logoUrl)
}

/**
 * The logo that stands in for it on a dark surface: one the customer uploaded, else the logo itself when it needs nothing behind it,
 * else the generated white copy, as GET /workspaces/my names it. Null: there is none, and the logo keeps its white plate.
 */
export function workspaceDarkLogoOf(
  workspaces: readonly ListedWorkspace[],
  activeWorkspaceId: string | null,
): string | null {
  if (activeWorkspaceId === 'all') return resolveBackendFileUrl(workspaces[0]?.companyDarkLogoUrl)
  return resolveBackendFileUrl(workspaces.find((w) => w.id === activeWorkspaceId)?.darkLogoUrl)
}

/**
 * workspaceLogoOf for the workspace list the sidebar's switcher keeps in the
 * store (TeamSwitcher syncs GET /workspaces/my there on every page with the
 * app shell). Null until the list arrives. A page without the shell reads the
 * list itself (useWorkspaces) and calls workspaceLogoOf.
 */
export function useWorkspaceLogo(): string | null {
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  return workspaceLogoOf(workspaces, activeWorkspaceId)
}

/** workspaceDarkLogoOf for the same list. */
export function useWorkspaceDarkLogo(): string | null {
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  return workspaceDarkLogoOf(workspaces, activeWorkspaceId)
}
